import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import ArticleCard from './components/ArticleCard';
import { DisplayAd } from './components/AdSense';
import { articles } from './data/articles';
import {
  useCurrentLang,
  fetchArticleMetadata,
  type ArticleMetadata,
} from './hooks/useArticleMetadata';

function Articles() {
  const { t } = useTranslation();
  const currentLang = useCurrentLang();

  const [articlesWithMetadata, setArticlesWithMetadata] = useState<
    ArticleMetadata[]
  >([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchArticlesMetadata = async () => {
      try {
        const metadataPromises = articles.map((article) =>
          fetchArticleMetadata(article.slug, currentLang)
        );

        const results = await Promise.all(metadataPromises);
        const articlesData = results.filter(
          (m): m is ArticleMetadata => m !== undefined
        );
        setArticlesWithMetadata(articlesData);
      } catch (error) {
        console.error('Error loading articles metadata:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchArticlesMetadata();
  }, [currentLang]);

  return (
    <>
      <Helmet>
        <title>Articles & Resources - ShiftPic</title>
        <meta name="description" content="Explore our collection of articles, guides, and resources to help you understand image formats, optimization techniques, and best practices for working with images on the web." />
        <meta name="keywords" content="image formats, image optimization, web images, AVIF, WebP, JPEG, PNG, SVG, GIF, image guides, image conversion tutorials" />
        <meta property="og:title" content="Articles & Resources - ShiftPic" />
        <meta property="og:description" content="Explore our collection of articles, guides, and resources to help you understand image formats, optimization techniques, and best practices for working with images on the web." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`https://shiftpic.com/${currentLang}/articles/`} />
        <meta property="og:image" content="https://shiftpic.com/og-image.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Articles & Resources - ShiftPic" />
        <meta name="twitter:description" content="Explore our collection of articles, guides, and resources to help you understand image formats, optimization techniques, and best practices for working with images on the web." />
        <meta name="twitter:image" content="https://shiftpic.com/og-image.jpg" />
        <link rel="canonical" href={`https://shiftpic.com/${currentLang}/articles/`} />
      </Helmet>

      <div className="container mx-auto px-4 py-8">
          <div className="max-w-[800px] mt-8 mb-16">
            <h1>
              {t('articles.title')}
            </h1>
            <p>
              {t('articles.description')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-32">
            {isLoading ? (
              // Loading skeletons
              Array(6).fill(0).map((_, index) => (
                <div key={index} className="bg-white rounded-card shadow-lg overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200"></div>
                  <div className="p-6">
                    <div className="h-4 bg-gray-200 rounded w-1/4 mb-4"></div>
                    <div className="h-6 bg-gray-200 rounded w-3/4 mb-2"></div>
                    <div className="h-4 bg-gray-200 rounded w-full mb-4"></div>
                    <div className="flex gap-2">
                      <div className="h-4 bg-gray-200 rounded w-20"></div>
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              articlesWithMetadata.map(article => (
              <ArticleCard 
                key={article.id} 
                article={article} 
                metadata={article} // Pass the pre-fetched metadata
              />
              ))
            )}
          </div>

          {/* AdSense Display Ad */}
          <div className="max-w-4xl mx-auto my-12">
            <DisplayAd className="mx-auto" />
          </div>
        </div>
    </>
  );
}

export default Articles;